import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Crown, Users, Lock, Unlock, Star, Zap } from 'lucide-react';

const API_BASE = '';

export default function PremiumConfig() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    fetch(`${API_BASE}/api/admin/premium/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500 text-center py-12">Loading...</div>;
  if (!data) return <div className="text-slate-500 text-center py-12">Failed to load</div>;

  return (
    <div data-testid="premium-config-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
          <Crown className="w-5 h-5 inline mr-2 text-amber-400" />
          Premium Infrastructure
        </h1>
        <p className="text-sm text-slate-500 mt-1">Configure tier limits and view user distribution</p>
      </div>

      {/* User Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatCard icon={Users} label="Total Users" value={data.stats.total_users} color="blue" />
        <StatCard icon={Crown} label="Premium" value={data.stats.premium_users} color="amber" />
        <StatCard icon={Users} label="Free" value={data.stats.free_users} color="slate" />
      </div>

      {/* Tier Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(data.tiers).map(([key, tier]) => (
          <div key={key} className={`rounded-xl border p-6 ${key === 'premium' ? 'border-amber-500/30 bg-amber-500/5' : 'border-slate-800 bg-slate-900/50'}`}
            data-testid={`tier-${key}`}>
            <div className="flex items-center gap-2 mb-4">
              {key === 'premium' ? <Crown className="w-5 h-5 text-amber-400" /> : <Users className="w-5 h-5 text-slate-400" />}
              <h2 className="text-lg font-bold text-slate-100">{tier.label}</h2>
              {key === 'premium' && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs ml-auto">Recommended</Badge>}
            </div>
            <div className="space-y-2 mb-4">
              <LimitRow label="Value Bets" value={tier.max_value_bets} isPremium={key === 'premium'} />
              <LimitRow label="Surebets" value={tier.max_surebets} isPremium={key === 'premium'} />
            </div>
            <div className="space-y-1.5">
              {tier.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Star className={`w-3 h-3 ${key === 'premium' ? 'text-amber-400' : 'text-slate-500'}`} />
                  <span className="text-slate-400">{f}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-slate-900/50 border border-slate-800 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium text-slate-300">Integration Ready</span>
        </div>
        <p className="text-xs text-slate-500">
          Premium infrastructure is prepared. API responses include <code className="text-cyan-400">is_premium</code> flags on value bets and surebets.
          Connect a payment provider (Stripe, etc.) to activate the paywall.
          Tier limits are configurable via the Config Management page.
        </p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = { blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20', amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20', slate: 'text-slate-300 bg-slate-700/50 border-slate-600' };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <Icon className="w-5 h-5 mb-2 opacity-60" />
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs opacity-60 mt-0.5">{label}</p>
    </div>
  );
}

function LimitRow({ label, value, isPremium }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-400">{label}</span>
      <div className="flex items-center gap-1.5">
        {value === -1 ? (
          <><Unlock className="w-3.5 h-3.5 text-emerald-400" /><span className="text-emerald-400 font-medium">Unlimited</span></>
        ) : (
          <><Lock className="w-3.5 h-3.5 text-slate-500" /><span className="text-slate-300 font-medium">{value}</span></>
        )}
      </div>
    </div>
  );
}
